import { ComponentType, CSSProperties, lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { Button, buttonVariants } from '@/components/ui/button'
import { Agent, AgentPrompt } from 'types/agent'
import { PreviewDocument } from 'components/notebook/DocumentPreviewContext'
import { PreviewLoading, PreviewUnavailable } from 'components/notebook/PreviewStates'
import type { SpreadsheetPreviewProps } from 'components/notebook/SpreadsheetPreview'
import { getFileIcon } from 'utils/chat-attachments'
import { getStoredFileName } from 'utils/citations'
import { DownloadFileButton } from 'components/messages/DownloadFileButton'
import { findIconDefinition, IconName } from '@fortawesome/fontawesome-svg-core'

const DEFAULT_PROMPT_ICON: IconName = 'wand-magic-sparkles'

/** Icon name typed in the backoffice, or the default when unset or unknown to the loaded `far` set. */
const promptIcon = (icon?: string): IconName => {
  const name = icon as IconName | undefined
  return name && findIconDefinition({ prefix: 'far', iconName: name }) ? name : DEFAULT_PROMPT_ICON
}

// Own chunk, fetched on the first spreadsheet only (it pulls in SheetJS). If it can't load, the panel
// shows "unavailable" instead of taking the notebook down with it.
const SpreadsheetPreview = lazy(() =>
  import('components/notebook/SpreadsheetPreview')
    .then((m): { default: ComponentType<SpreadsheetPreviewProps> } => ({ default: m.SpreadsheetPreview }))
    .catch((e) => {
      console.error(e)
      return { default: PreviewUnavailable }
    }),
)

type NotebookPanelProps = {
  agent: Agent
  disabled?: boolean
  onPromptClick: (prompt: AgentPrompt) => void
  /** When set, the panel shows this document (PDF or spreadsheet) instead of the prompt cards. */
  doc?: PreviewDocument
  onCloseDocument: () => void
}

/** Right panel of the notebook: the agent's prompt cards, or the document the user clicked in a message. */
export const NotebookPanel = ({ agent, disabled, onPromptClick, doc, onCloseDocument }: NotebookPanelProps) => {
  const { t } = useTranslation()
  const prompts = agent.prompts ?? []

  if (doc) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex h-[60px] shrink-0 items-center gap-1 border-b border-border px-3">
          <OdinIcon icon={['fas', getFileIcon(doc.sourceUrl || doc.name)]} className="h-4 w-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground" title={doc.name}>
            {doc.name}
          </span>
          <DownloadFileButton url={doc.sourceUrl} fileName={getStoredFileName(doc.sourceUrl) || doc.name} />
          {doc.url && (
            <a
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              title={t('notebookOpenInNewTab')}
              className={buttonVariants({ variant: 'ghost', size: 'icon-sm' })}
            >
              <OdinIcon icon={['far', 'arrow-up-right-from-square']} className="h-4 w-4" />
            </a>
          )}
          <Button variant="ghost" size="icon-sm" onClick={onCloseDocument} title={t('close')}>
            <OdinIcon icon={['far', 'xmark']} className="h-4 w-4" />
          </Button>
        </div>
        {!doc.url ? (
          <PreviewLoading />
        ) : doc.kind === 'pdf' ? (
          // PDF open parameter: the viewer's thumbnails sidebar stays closed, only the document shows
          <iframe src={`${doc.url}#navpanes=0`} title={doc.name} className="min-h-0 w-full flex-1 border-0 bg-muted" />
        ) : (
          <Suspense fallback={<PreviewLoading />}>
            <SpreadsheetPreview key={doc.url} url={doc.url} />
          </Suspense>
        )}
      </div>
    )
  }

  return (
    // `@container` here, not on the grid: a container query only reads an ancestor's size
    <div className="@container flex h-full min-h-0 flex-col">
      {/* same header as the document view, so the panel keeps its shape when switching */}
      <div className="flex h-[60px] shrink-0 items-center gap-2 border-b border-border px-3">
        <OdinIcon icon={['far', 'wand-magic-sparkles']} className="h-4 w-4 shrink-0 text-primary" />
        <span className="text-sm font-semibold text-foreground">{t('notebookPromptsTitle')}</span>
      </div>
      {/* NotebookLM-style tiles: colored icon over the label, chevron on the right; the full prompt text
          is only in the tooltip. `--tile` drives both the icon and the tint. Two per row only when the
          (resizable) panel is wide enough, container query so it follows a drag, not the viewport. */}
      <div className="scrollbar-hidden grid min-h-0 grid-cols-1 content-start gap-2 overflow-y-auto p-3 @min-[330px]:grid-cols-2">
        {prompts.map((prompt) => (
          // ghost Button for focus ring / disabled state; the tint classes override its hover and height.
          // Icons use `size-*` so the Button's `[&_svg]:size-4` rule leaves them alone.
          <Button
            key={prompt.key}
            variant="ghost"
            disabled={disabled}
            title={prompt.text}
            onClick={() => onPromptClick(prompt)}
            style={{ '--tile': prompt.color || 'var(--primary)' } as CSSProperties}
            className="h-auto justify-start gap-2 rounded-xl bg-[color-mix(in_oklab,var(--tile)_8%,var(--background))] py-2 pr-2 pl-3 text-left whitespace-normal duration-200 hover:border-[var(--tile)] hover:bg-[color-mix(in_oklab,var(--tile)_14%,var(--background))] disabled:opacity-60"
          >
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <OdinIcon icon={['far', promptIcon(prompt.icon)]} className="size-3.5 text-[var(--tile)]" />
              <span className="line-clamp-2 text-[13px] leading-tight font-medium text-foreground">{prompt.label}</span>
            </span>
            <OdinIcon icon={['far', 'angle-right']} className="size-3.5 shrink-0 text-muted-foreground" />
          </Button>
        ))}
      </div>
    </div>
  )
}
